// The date of an activity (Outreach Log › Date), one rule for title, detail, order and counts (pure, tested by
// scripts/test-activity-date.mjs; agreed 04-11-2026). The precision of a value comes from its demonstrable origin,
// never from the value alone (a real email can be sent at exactly 00:00:00.000 UTC):
//   - instant: any time other than midnight UTC, or a value Oike wrote as a real instant (INSTANT_MARK in Notes:
//     Gmail's own time, or the moment Ale registered something) → shown in Hoy's zone, also at midnight UTC;
//   - day: a plain «YYYY-MM-DD», or a Gmail activity ([gmsg:…]) created before PRECISION_SINCE, when every Oike Gmail
//     writer stored only the day (Airtable keeps it as midnight UTC) → that day, never shifted;
//   - ambiguous: midnight UTC without either proof → shown as stored («5 nov 2026 · 00:00 UTC»), its UTC date used
//     to order and count; never converted, never reclassified, never rewritten.
// Nothing adds or subtracts days, and Airtable's zone is never changed to compensate.
import { localDay, isValidTimeZone } from './coachingTime.js';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** In Notes: «Date holds a real instant» (Oike writes it with every instant it stores, from 04-11-2026). */
export const INSTANT_MARK = '[fecha:instante]';
/** Before this moment (the commit that started storing Gmail instants) Oike's Gmail writers stored only the day. */
export const PRECISION_SINCE = '2026-11-04T08:46:34Z';
const MIDNIGHT_UTC = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.000)?Z$/;
const val = (o, k) => { const v = o?.fields?.[k]; return v && typeof v === 'object' && !Array.isArray(v) ? (v.name ?? v.value ?? '') : (v ?? ''); };

/** Hoy's zone (Mi espacio › Hoy, saved in this browser), or the browser's. */
export function todayTimeZone() {
  try { const saved = localStorage.getItem('oike_myday_tz'); if (isValidTimeZone(saved)) return saved; } catch { /* no storage */ }
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

/**
 * { kind: 'day', day } | { kind: 'instant', ms } | { kind: 'ambiguous', day, ms } | null. `origin` is what proves the
 * precision: { notes, createdTime } of the record (without it, a midnight UTC value is ambiguous).
 */
export function parseActivityDate(value, origin = {}) {
  const v = String(value || '').trim();
  if (!v) return null;
  if (DAY.test(v)) return { kind: 'day', day: v };
  const ms = new Date(v).getTime();
  if (Number.isNaN(ms)) return null;
  const m = v.match(MIDNIGHT_UTC);
  if (!m) return { kind: 'instant', ms };
  const notes = String(origin.notes || '');
  if (notes.includes(INSTANT_MARK)) return { kind: 'instant', ms };
  const created = Date.parse(String(origin.createdTime || ''));
  if (/\[gmsg:[^\]]+\]/.test(notes) && Number.isFinite(created) && created < Date.parse(PRECISION_SINCE)) return { kind: 'day', day: m[1] };
  return { kind: 'ambiguous', day: m[1], ms };
}
export const activityDateOf = (o) => parseActivityDate(val(o, 'Date'), { notes: val(o, 'Notes'), createdTime: o?.createdTime });

/** The local day (YYYY-MM-DD) in a zone: a day stays itself; an instant is converted. */
export function activityDay(o, tz = todayTimeZone()) {
  const d = typeof o === 'string' ? parseActivityDate(o) : activityDateOf(o);
  if (!d) return null;
  if (d.kind === 'day' || d.kind === 'ambiguous') return d.day;
  return localDay(new Date(d.ms), isValidTimeZone(tz) ? tz : 'UTC').day;
}

/** For ordering: an instant by its moment; a day at its noon UTC (between the instants of that same day everywhere). */
export function activitySortKey(o) {
  const d = activityDateOf(o);
  if (!d) return 0;
  return d.kind === 'day' ? Date.parse(`${d.day}T12:00:00Z`) : d.ms;
}
export const byActivityDateDesc = (a, b) => activitySortKey(b) - activitySortKey(a);

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
const fmtDay = (day) => { const [y, m, d] = day.split('-').map(Number); return `${d} ${MONTHS[m - 1]} ${y}`; };
/** «4 nov 2026» or «4 nov 2026, 10:32» (instant, in the zone); never the ambiguous US format. */
export function formatActivityDate(o, tz = todayTimeZone(), { time = true } = {}) {
  const d = typeof o === 'string' ? parseActivityDate(o) : activityDateOf(o);
  if (!d) return '';
  if (d.kind === 'day') return fmtDay(d.day);
  if (d.kind === 'ambiguous') return `${fmtDay(d.day)} · 00:00 UTC`; // as stored: neither converted nor read as a day
  const zone = isValidTimeZone(tz) ? tz : 'UTC';
  const day = localDay(new Date(d.ms), zone).day;
  if (!time) return fmtDay(day);
  const hm = new Intl.DateTimeFormat('es', { timeZone: zone, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(d.ms));
  return `${fmtDay(day)}, ${hm}`;
}

/** A value known to be a real instant (Gmail's time of a message): always in Hoy's zone, also at midnight UTC. */
export const formatInstant = (iso, tz = todayTimeZone()) => formatActivityDate({ fields: { Date: iso, Notes: INSTANT_MARK } }, tz);

/**
 * The title without the date older records carry in their name («Email — 11/4/2026», «… — 2026-11-03»): the date
 * shown is always the one of the Date field. The stored name is not changed.
 */
export function activityTitle(o) {
  const name = String(val(o, 'Activity Name') || '').trim();
  return name.replace(/\s+—\s+(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})$/, '').trim();
}

/** Counts by day in Hoy's zone (today / this week), from the same date. */
export function isOnOrAfterDay(o, day, tz = todayTimeZone()) {
  const d = activityDay(o, tz);
  return !!d && d >= day;
}
